/**
 * Settings Drawer
 * Slide-out drawer for CRM settings including Stripe payment integration
 */

import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Payment as PaymentIcon,
  Notifications as NotificationsIcon,
  Business as BusinessIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showToast } from '../utils/toast';
import {
  getPaymentGatewayConfig,
  savePaymentGatewayConfig as savePaymentGatewayConfigApi,
  testPaymentGatewayConnection,
  type SavePaymentGatewayConfigRequest
} from '../api/paymentGatewayApi';

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Mock payment methods for demo
const mockPaymentMethods = [
  { id: 'pm_1', brand: 'visa', last4: '4242', expMonth: 12, expYear: 2025, isDefault: true },
  { id: 'pm_2', brand: 'mastercard', last4: '5555', expMonth: 3, expYear: 2026, isDefault: false }
];

// Stripe configuration interface - local state for form inputs
interface StripeConfigForm {
  isTestMode: boolean;
  // Test mode keys
  testPublishableKey: string;
  testSecretKey: string;
  testWebhookSecret: string;
  // Live mode keys
  livePublishableKey: string;
  liveSecretKey: string;
  liveWebhookSecret: string;
  // Status
  isConfigured: boolean;
}

export const SettingsDrawer: React.FC<SettingsDrawerProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [addPaymentDialogOpen, setAddPaymentDialogOpen] = useState(false);
  const [showTestSecretKey, setShowTestSecretKey] = useState(false);
  const [showLiveSecretKey, setShowLiveSecretKey] = useState(false);
  const [showTestWebhookSecret, setShowTestWebhookSecret] = useState(false);
  const [showLiveWebhookSecret, setShowLiveWebhookSecret] = useState(false);

  // Stripe configuration state
  const [stripeConfig, setStripeConfig] = useState<StripeConfigForm>({
    isTestMode: true,
    testPublishableKey: '',
    testSecretKey: '',
    testWebhookSecret: '',
    livePublishableKey: '',
    liveSecretKey: '',
    liveWebhookSecret: '',
    isConfigured: false
  });

  // Notification settings state
  const [notifications, setNotifications] = useState({
    emailLeadAssigned: true,
    emailNewLead: true,
    emailTaskDue: true,
    emailWeeklyReport: false,
    pushNewLead: true,
    pushTaskReminder: true,
    pushClientAtRisk: true
  });

  // Company settings state
  const [companySettings, setCompanySettings] = useState({
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    companyAddress: '',
    defaultCurrency: 'HKD',
    timezone: 'Asia/Kuala_Lumpur'
  });

  // Fetch payment gateway config from API
  const { data: gatewayConfig, isLoading: loadingConfig } = useQuery({
    queryKey: ['payment-gateway-config'],
    queryFn: getPaymentGatewayConfig,
    staleTime: 5 * 60 * 1000,
    enabled: open
  });

  // Sync form state when gateway config is loaded
  useEffect(() => {
    if (gatewayConfig) {
      setStripeConfig({
        isTestMode: gatewayConfig.isTestMode,
        testPublishableKey: gatewayConfig.testPublishableKey || '',
        testSecretKey: '', // Don't populate - only show if user wants to update
        testWebhookSecret: '',
        livePublishableKey: gatewayConfig.livePublishableKey || '',
        liveSecretKey: '',
        liveWebhookSecret: '',
        isConfigured: gatewayConfig.isConfigured
      });
    }
  }, [gatewayConfig]);

  // Mock query for payment methods
  const { data: paymentMethods = mockPaymentMethods, isLoading: loadingPayments } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: async () => mockPaymentMethods,
    staleTime: 5 * 60 * 1000,
    enabled: open
  });

  // Save Stripe configuration
  const saveStripeMutation = useMutation({
    mutationFn: async (config: SavePaymentGatewayConfigRequest) => {
      return await savePaymentGatewayConfigApi(config);
    },
    onSuccess: () => {
      showToast.success('Stripe configuration saved successfully');
      setStripeConfig(prev => ({ ...prev, isConfigured: true }));
      queryClient.invalidateQueries({ queryKey: ['payment-gateway-config'] });
    },
    onError: (error: any) => {
      showToast.operationFailed(error?.response?.data?.message || 'Failed to save Stripe configuration');
    }
  });

  // Test Stripe connection
  const testConnectionMutation = useMutation({
    mutationFn: testPaymentGatewayConnection,
    onSuccess: (data) => {
      showToast.success(`Connected to Stripe (${data.mode} mode)`);
    },
    onError: (error: any) => {
      showToast.operationFailed(error?.response?.data?.message || 'Failed to connect to Stripe');
    }
  });

  // Save notification settings
  const saveNotificationsMutation = useMutation({
    mutationFn: async (settings: typeof notifications) => {
      await new Promise(resolve => setTimeout(resolve, 500));
      return settings;
    },
    onSuccess: () => {
      showToast.success('Notification settings saved');
    }
  });

  // Delete payment method
  const deletePaymentMethodMutation = useMutation({
    mutationFn: async (id: string) => {
      await new Promise(resolve => setTimeout(resolve, 500));
      return id;
    },
    onSuccess: () => {
      showToast.success('Payment method removed');
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    }
  });

  // Set default payment method
  const setDefaultPaymentMutation = useMutation({
    mutationFn: async (id: string) => {
      await new Promise(resolve => setTimeout(resolve, 500));
      return id;
    },
    onSuccess: () => {
      showToast.success('Default payment method updated');
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    }
  });

  const handleSaveStripeConfig = () => {
    // Validate based on mode - allow keeping existing secret key
    if (stripeConfig.isTestMode) {
      const hasTestSecretKey = stripeConfig.testSecretKey || gatewayConfig?.hasTestSecretKey;
      if (!stripeConfig.testPublishableKey || !hasTestSecretKey) {
        showToast.operationFailed('Please enter both Test Publishable Key and Test Secret Key');
        return;
      }
    } else {
      const hasLiveSecretKey = stripeConfig.liveSecretKey || gatewayConfig?.hasLiveSecretKey;
      if (!stripeConfig.livePublishableKey || !hasLiveSecretKey) {
        showToast.operationFailed('Please enter both Live Publishable Key and Live Secret Key');
        return;
      }
    }

    const request: SavePaymentGatewayConfigRequest = {
      isTestMode: stripeConfig.isTestMode,
      testPublishableKey: stripeConfig.testPublishableKey || undefined,
      testSecretKey: stripeConfig.testSecretKey || undefined,
      testWebhookSecret: stripeConfig.testWebhookSecret || undefined,
      livePublishableKey: stripeConfig.livePublishableKey || undefined,
      liveSecretKey: stripeConfig.liveSecretKey || undefined,
      liveWebhookSecret: stripeConfig.liveWebhookSecret || undefined,
    };

    saveStripeMutation.mutate(request);
  };

  const handleTestConnection = () => {
    testConnectionMutation.mutate();
  };

  const handleSaveNotifications = () => {
    saveNotificationsMutation.mutate(notifications);
  };

  const getBrandIcon = (_brand: string) => {
    return <CreditCardIcon />;
  };

  const handleClose = () => {
    setActiveTab(0);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 550, md: 650 } }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage CRM settings and payment integration
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Stripe" icon={<PaymentIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
            <Tab label="Cards" icon={<CreditCardIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
            <Tab label="Alerts" icon={<NotificationsIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
            <Tab label="Company" icon={<BusinessIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {/* Payment Integration Tab - Stripe Configuration */}
          <TabPanel value={activeTab} index={0}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PaymentIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  Stripe Integration
                </Typography>
                {stripeConfig.isConfigured && (
                  <Chip label="Connected" color="success" size="small" icon={<CheckIcon />} />
                )}
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                Connect your Stripe account to enable payment processing. Get your API keys from the{' '}
                <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer">
                  Stripe Dashboard
                </a>.
              </Alert>

              <FormControlLabel
                control={
                  <Switch
                    checked={stripeConfig.isTestMode}
                    onChange={(e) => setStripeConfig(prev => ({ ...prev, isTestMode: e.target.checked }))}
                    color="warning"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Test Mode</Typography>
                    {stripeConfig.isTestMode && <Chip label="Testing" color="warning" size="small" />}
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              {/* Test Mode Keys */}
              {stripeConfig.isTestMode && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Test Mode Keys
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Test Publishable Key"
                      placeholder="pk_test_..."
                      value={stripeConfig.testPublishableKey}
                      onChange={(e) => setStripeConfig(prev => ({ ...prev, testPublishableKey: e.target.value }))}
                      helperText="Starts with pk_test_"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Test Secret Key"
                      placeholder="sk_test_... or rk_test_..."
                      type={showTestSecretKey ? 'text' : 'password'}
                      value={stripeConfig.testSecretKey}
                      onChange={(e) => setStripeConfig(prev => ({ ...prev, testSecretKey: e.target.value }))}
                      helperText={gatewayConfig?.hasTestSecretKey ? 'Leave empty to keep existing key' : 'Starts with sk_test_ or rk_test_'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowTestSecretKey(!showTestSecretKey)} edge="end" size="small">
                              {showTestSecretKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Test Webhook Secret (Optional)"
                      placeholder="whsec_..."
                      type={showTestWebhookSecret ? 'text' : 'password'}
                      value={stripeConfig.testWebhookSecret}
                      onChange={(e) => setStripeConfig(prev => ({ ...prev, testWebhookSecret: e.target.value }))}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowTestWebhookSecret(!showTestWebhookSecret)} edge="end" size="small">
                              {showTestWebhookSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Live Mode Keys */}
              {!stripeConfig.isTestMode && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                      Live Mode Keys (Production)
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Live Publishable Key"
                      placeholder="pk_live_..."
                      value={stripeConfig.livePublishableKey}
                      onChange={(e) => setStripeConfig(prev => ({ ...prev, livePublishableKey: e.target.value }))}
                      helperText="Starts with pk_live_"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Live Secret Key"
                      placeholder="sk_live_... or rk_live_..."
                      type={showLiveSecretKey ? 'text' : 'password'}
                      value={stripeConfig.liveSecretKey}
                      onChange={(e) => setStripeConfig(prev => ({ ...prev, liveSecretKey: e.target.value }))}
                      helperText={gatewayConfig?.hasLiveSecretKey ? 'Leave empty to keep existing key' : 'Starts with sk_live_ or rk_live_ - Keep secure!'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowLiveSecretKey(!showLiveSecretKey)} edge="end" size="small">
                              {showLiveSecretKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Live Webhook Secret (Optional)"
                      placeholder="whsec_..."
                      type={showLiveWebhookSecret ? 'text' : 'password'}
                      value={stripeConfig.liveWebhookSecret}
                      onChange={(e) => setStripeConfig(prev => ({ ...prev, liveWebhookSecret: e.target.value }))}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowLiveWebhookSecret(!showLiveWebhookSecret)} edge="end" size="small">
                              {showLiveWebhookSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                </Grid>
              )}

              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={saveStripeMutation.isPending ? <CircularProgress size={14} /> : <SaveIcon />}
                  onClick={handleSaveStripeConfig}
                  disabled={saveStripeMutation.isPending}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={testConnectionMutation.isPending ? <CircularProgress size={14} /> : <RefreshIcon />}
                  onClick={handleTestConnection}
                  disabled={testConnectionMutation.isPending || !stripeConfig.isConfigured}
                >
                  Test Connection
                </Button>
              </Box>
            </Paper>

            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Integration Status ({stripeConfig.isTestMode ? 'Test Mode' : 'Live Mode'})
                </Typography>
                <List dense disablePadding>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {(stripeConfig.isTestMode ? stripeConfig.testPublishableKey : stripeConfig.livePublishableKey) || (stripeConfig.isTestMode ? gatewayConfig?.testPublishableKey : gatewayConfig?.livePublishableKey)
                        ? <CheckIcon color="success" fontSize="small" />
                        : <WarningIcon color="warning" fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={stripeConfig.isTestMode ? 'Test Publishable Key' : 'Live Publishable Key'}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {(stripeConfig.isTestMode ? gatewayConfig?.hasTestSecretKey : gatewayConfig?.hasLiveSecretKey)
                        ? <CheckIcon color="success" fontSize="small" />
                        : <WarningIcon color="warning" fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={stripeConfig.isTestMode ? 'Test Secret Key' : 'Live Secret Key'}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {(stripeConfig.isTestMode ? gatewayConfig?.hasTestWebhookSecret : gatewayConfig?.hasLiveWebhookSecret)
                        ? <CheckIcon color="success" fontSize="small" />
                        : <WarningIcon color="disabled" fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText primary="Webhook Secret (Optional)" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                </List>
                {gatewayConfig?.lastTestedAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Last tested: {new Date(gatewayConfig.lastTestedAt).toLocaleString()} -
                    {gatewayConfig.lastTestResult === 'SUCCESS'
                      ? <Chip label="Success" color="success" size="small" sx={{ ml: 1, height: 18 }} />
                      : <Chip label="Failed" color="error" size="small" sx={{ ml: 1, height: 18 }} />}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Webhook URL
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={`${window.location.origin}/api/v1/webhooks/stripe`}
                  InputProps={{ readOnly: true }}
                />
              </CardContent>
            </Card>
          </TabPanel>

          {/* Payment Methods Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Payment Methods
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddPaymentDialogOpen(true)}
              >
                Add Card
              </Button>
            </Box>

            {loadingPayments ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : paymentMethods.length === 0 ? (
              <Alert severity="info">
                No payment methods added yet.
              </Alert>
            ) : (
              <List disablePadding>
                {paymentMethods.map((method) => (
                  <ListItem
                    key={method.id}
                    sx={{
                      border: 1,
                      borderColor: method.isDefault ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: method.isDefault ? 'primary.light' : 'transparent'
                    }}
                  >
                    <ListItemIcon>
                      {getBrandIcon(method.brand)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {method.brand.toUpperCase()} **** {method.last4}
                          </Typography>
                          {method.isDefault && (
                            <Chip label="Default" size="small" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={`Expires ${method.expMonth}/${method.expYear}`}
                    />
                    <ListItemSecondaryAction>
                      {!method.isDefault && (
                        <Button
                          size="small"
                          onClick={() => setDefaultPaymentMutation.mutate(method.id)}
                          sx={{ mr: 1 }}
                        >
                          Set Default
                        </Button>
                      )}
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => deletePaymentMethodMutation.mutate(method.id)}
                        disabled={method.isDefault}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </TabPanel>

          {/* Notifications Tab */}
          <TabPanel value={activeTab} index={2}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Email Notifications
              </Typography>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary="Lead Assigned"
                    secondary="Email when a lead is assigned to you"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Switch
                    size="small"
                    checked={notifications.emailLeadAssigned}
                    onChange={(e) => setNotifications(prev => ({ ...prev, emailLeadAssigned: e.target.checked }))}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText
                    primary="New Lead"
                    secondary="Email when a new lead is created"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Switch
                    size="small"
                    checked={notifications.emailNewLead}
                    onChange={(e) => setNotifications(prev => ({ ...prev, emailNewLead: e.target.checked }))}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText
                    primary="Task Due Reminder"
                    secondary="Email reminders for upcoming tasks"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Switch
                    size="small"
                    checked={notifications.emailTaskDue}
                    onChange={(e) => setNotifications(prev => ({ ...prev, emailTaskDue: e.target.checked }))}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText
                    primary="Weekly Report"
                    secondary="Weekly performance summary"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Switch
                    size="small"
                    checked={notifications.emailWeeklyReport}
                    onChange={(e) => setNotifications(prev => ({ ...prev, emailWeeklyReport: e.target.checked }))}
                  />
                </ListItem>
              </List>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Push Notifications
              </Typography>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary="New Lead Alert"
                    secondary="Notify when new leads come in"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Switch
                    size="small"
                    checked={notifications.pushNewLead}
                    onChange={(e) => setNotifications(prev => ({ ...prev, pushNewLead: e.target.checked }))}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText
                    primary="Task Reminder"
                    secondary="Remind about tasks due soon"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Switch
                    size="small"
                    checked={notifications.pushTaskReminder}
                    onChange={(e) => setNotifications(prev => ({ ...prev, pushTaskReminder: e.target.checked }))}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText
                    primary="Client At Risk"
                    secondary="Alert when churn risk increases"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Switch
                    size="small"
                    checked={notifications.pushClientAtRisk}
                    onChange={(e) => setNotifications(prev => ({ ...prev, pushClientAtRisk: e.target.checked }))}
                  />
                </ListItem>
              </List>
            </Paper>

            <Button
              variant="contained"
              size="small"
              startIcon={saveNotificationsMutation.isPending ? <CircularProgress size={14} /> : <SaveIcon />}
              onClick={handleSaveNotifications}
              disabled={saveNotificationsMutation.isPending}
            >
              Save Notifications
            </Button>
          </TabPanel>

          {/* Company Tab */}
          <TabPanel value={activeTab} index={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Company Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Company Name"
                    value={companySettings.companyName}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, companyName: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Company Email"
                    type="email"
                    value={companySettings.companyEmail}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, companyEmail: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone Number"
                    value={companySettings.companyPhone}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, companyPhone: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Default Currency"
                    value={companySettings.defaultCurrency}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, defaultCurrency: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Business Address"
                    multiline
                    rows={2}
                    value={companySettings.companyAddress}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, companyAddress: e.target.value }))}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" size="small" startIcon={<SaveIcon />}>
                  Save Company Settings
                </Button>
              </Box>
            </Paper>
          </TabPanel>
        </Box>

        {/* Add Payment Method Dialog */}
        <Dialog open={addPaymentDialogOpen} onClose={() => setAddPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Payment Method</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              This is a demo. In production, this would show a Stripe Elements payment form.
            </Alert>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Configure Stripe API keys" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Stripe Elements will load securely" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Enter card details" />
              </ListItem>
              <ListItem>
                <ListItemText primary="4. Card is saved to your account" />
              </ListItem>
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddPaymentDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                showToast.success('Demo: Payment method would be added here');
                setAddPaymentDialogOpen(false);
              }}
            >
              Add Card (Demo)
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Drawer>
  );
};

export default SettingsDrawer;
